import { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { cloneOccupancies } from 'lib/offer/occupancyUtils'
import type { ListingDrawerHotelDateSelection } from '../components/ListingDrawerSearchDates'

interface Params {
  searchDispatch: React.Dispatch<GlobalSearchAction>
  committedHotelDateSelection: ListingDrawerHotelDateSelection
  defaultRooms: Array<App.Occupants>
  isCompactDatePicker: boolean
  commitHotelDateSelection: (selectionOverride?: ListingDrawerHotelDateSelection) => void
  commitRoomsToParent: () => void
  areRoomsValid: boolean
  draftRooms: Array<App.Occupants>
  initiallyOpenGuestsPanel?: boolean
}

function getDateSearchOptionId(
  selection: ListingDrawerHotelDateSelection,
): DATE_SEARCH_OPTION_IDS {
  if (selection.type === 'specific') return DATE_SEARCH_OPTION_IDS.SPECIFIC
  if (selection.type === 'flexible') return DATE_SEARCH_OPTION_IDS.FLEXIBLE
  return DATE_SEARCH_OPTION_IDS.ANYTIME
}

function useListingDrawerHotelSearchDraft({
  searchDispatch,
  committedHotelDateSelection,
  defaultRooms,
  isCompactDatePicker,
  commitHotelDateSelection,
  commitRoomsToParent,
  areRoomsValid,
  draftRooms,
  initiallyOpenGuestsPanel = false,
}: Params) {
  const [isHotelDatePanelOpen, setIsHotelDatePanelOpen] = useState(false)
  const [isHotelGuestsPanelOpen, setIsHotelGuestsPanelOpen] = useState(initiallyOpenGuestsPanel)
  const [isExperienceDatePanelOpen, setIsExperienceDatePanelOpen] = useState(false)
  const committedRoomsRef = useRef(defaultRooms)

  useEffect(() => {
    committedRoomsRef.current = defaultRooms
  }, [defaultRooms])

  const restoreCommittedDates = useCallback(() => {
    if (committedHotelDateSelection.type === 'specific') {
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: moment(committedHotelDateSelection.checkIn) })
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: moment(committedHotelDateSelection.checkOut) })
    } else {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    }

    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })

    if (committedHotelDateSelection.type === 'flexible') {
      searchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: committedHotelDateSelection.flexibleNights,
      })
      if (committedHotelDateSelection.flexibleMonths) {
        searchDispatch({
          type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
          flexibleMonths: committedHotelDateSelection.flexibleMonths,
        })
      }
      if (committedHotelDateSelection.userSelectedFlexibleMonths) {
        searchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
      }
    }

    searchDispatch({
      type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
      optionId: getDateSearchOptionId(committedHotelDateSelection),
    })
    searchDispatch({
      type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED,
      selected: committedHotelDateSelection.type === 'anytime',
    })
  }, [committedHotelDateSelection, searchDispatch])

  const restoreCommittedRooms = useCallback(() => {
    searchDispatch({
      type: GlobalSearchStateActions.SET_OCCUPANCIES,
      occupancies: cloneOccupancies(committedRoomsRef.current),
    })
  }, [searchDispatch])

  const clearRoomErrors = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: false })
  }, [searchDispatch])

  const handleRoomChange = useCallback((occupancies: Array<App.Occupants>) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies })
  }, [searchDispatch])

  useEffect(() => {
    if (isHotelDatePanelOpen || isExperienceDatePanelOpen || isHotelGuestsPanelOpen) return
    restoreCommittedDates()
  }, [
    isExperienceDatePanelOpen,
    isHotelDatePanelOpen,
    isHotelGuestsPanelOpen,
    restoreCommittedDates,
  ])

  useEffect(() => {
    if (isHotelGuestsPanelOpen) return
    restoreCommittedRooms()
  }, [defaultRooms, isHotelGuestsPanelOpen, restoreCommittedRooms])

  useEffect(() => {
    if (isCompactDatePicker) return

    setIsHotelDatePanelOpen(false)
    setIsHotelGuestsPanelOpen(false)
    setIsExperienceDatePanelOpen(false)
  }, [isCompactDatePicker])

  const openHotelDatePanel = useCallback(() => {
    restoreCommittedRooms()
    // Don't clear Guests errors while occupancy is still incomplete (e.g. unset ages).
    if (areRoomsValid) {
      clearRoomErrors()
    }
    setIsHotelGuestsPanelOpen(false)
    setIsHotelDatePanelOpen(true)
  }, [areRoomsValid, clearRoomErrors, restoreCommittedRooms])

  const closeHotelDatePanel = useCallback(() => {
    restoreCommittedDates()
    setIsHotelDatePanelOpen(false)
  }, [restoreCommittedDates])

  const confirmHotelDatePanel = useCallback((selectionOverride?: ListingDrawerHotelDateSelection) => {
    commitHotelDateSelection(selectionOverride)
    setIsHotelDatePanelOpen(false)
  }, [commitHotelDateSelection])

  const openHotelGuestsPanel = useCallback(() => {
    restoreCommittedDates()
    setIsHotelDatePanelOpen(false)
    setIsHotelGuestsPanelOpen(true)
  }, [restoreCommittedDates])

  const closeHotelGuestsPanel = useCallback(() => {
    restoreCommittedRooms()
    // Leave areRoomErrorsShown alone — ListingDrawerSearchControls clears it when valid,
    // and Apply/Search/initial seed are what set it.
    setIsHotelGuestsPanelOpen(false)
  }, [restoreCommittedRooms])

  const confirmHotelGuestsPanel = useCallback(() => {
    if (!areRoomsValid) {
      searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }

    commitRoomsToParent()
    committedRoomsRef.current = cloneOccupancies(draftRooms)
    setIsHotelGuestsPanelOpen(false)
  }, [areRoomsValid, commitRoomsToParent, draftRooms, searchDispatch])

  const openExperienceDatePanel = useCallback(() => {
    setIsExperienceDatePanelOpen(true)
  }, [])

  const closeExperienceDatePanel = useCallback(() => {
    restoreCommittedDates()
    setIsExperienceDatePanelOpen(false)
  }, [restoreCommittedDates])

  const confirmExperienceDatePanel = useCallback(() => {
    commitHotelDateSelection()
    setIsExperienceDatePanelOpen(false)
  }, [commitHotelDateSelection])

  return {
    isHotelDatePanelOpen,
    isHotelGuestsPanelOpen,
    isExperienceDatePanelOpen,
    restoreCommittedDates,
    clearRoomErrors,
    handleRoomChange,
    openHotelDatePanel,
    closeHotelDatePanel,
    confirmHotelDatePanel,
    openHotelGuestsPanel,
    closeHotelGuestsPanel,
    confirmHotelGuestsPanel,
    openExperienceDatePanel,
    closeExperienceDatePanel,
    confirmExperienceDatePanel,
  }
}

export default useListingDrawerHotelSearchDraft
